/**
 * Préférences de listes par utilisateur : nombre d'éléments par page,
 * réglé indépendamment pour chaque type de liste et persisté dans le profil.
 *
 * Singleton de module (même approche que `useAuth`) : l'état est partagé entre
 * toutes les vues. Préchargé au démarrage via `useAuth.checkAuth()` (awaité par
 * le guard de routeur) → la valeur est disponible avant le 1er fetch d'une liste.
 */

import { ref } from 'vue'
import { userPreferencesService } from '../services/userPreferencesService.js'

// Options / libellés partagés avec le backend (whitelist des valeurs persistées).
import {
  PAGE_SIZE_OPTIONS,
  DEFAULT_PAGE_SIZE,
  ALL_PAGE_SIZE,
  pageSizeLabel,
} from '@shared/list-page-sizes.js'

export { PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE, ALL_PAGE_SIZE, pageSizeLabel }

// État partagé (niveau module).
const preferences = ref({ listPageSizes: {} })
const loaded = ref(false)
let loadPromise = null

export function useListPreferences() {
  /**
   * Charge les préférences une seule fois. Silencieux en cas d'erreur
   * (on retombe alors sur les valeurs par défaut).
   */
  const loadPreferences = async () => {
    if (loaded.value) return preferences.value
    if (loadPromise) return loadPromise

    loadPromise = userPreferencesService
      .get()
      .then((data) => {
        preferences.value = { listPageSizes: (data && data.listPageSizes) || {} }
        loaded.value = true
        return preferences.value
      })
      .catch((err) => {
        console.error('Chargement des préférences de listes échoué:', err)
        loaded.value = true // évite un re-fetch en boucle, on utilise les défauts
        return preferences.value
      })
      .finally(() => {
        loadPromise = null
      })

    return loadPromise
  }

  /**
   * Taille de page pour une liste donnée, ou la valeur par défaut.
   */
  const getPageSize = (listKey) => {
    const value = preferences.value.listPageSizes[listKey]
    return PAGE_SIZE_OPTIONS.includes(value) ? value : DEFAULT_PAGE_SIZE
  }

  /**
   * Met à jour la taille de page d'une liste : maj locale optimiste immédiate
   * + persistance best-effort côté serveur.
   */
  const setPageSize = (listKey, value) => {
    const size = Number(value)
    if (!PAGE_SIZE_OPTIONS.includes(size)) return

    preferences.value = {
      ...preferences.value,
      listPageSizes: { ...preferences.value.listPageSizes, [listKey]: size },
    }

    userPreferencesService
      .update({ listPageSizes: { [listKey]: size } })
      .catch((err) => console.error('Sauvegarde de la préférence de liste échouée:', err))
  }

  /**
   * Réinitialise l'état (au logout).
   */
  const resetPreferences = () => {
    preferences.value = { listPageSizes: {} }
    loaded.value = false
    loadPromise = null
  }

  return {
    preferences,
    loaded,
    loadPreferences,
    getPageSize,
    setPageSize,
    resetPreferences,
    PAGE_SIZE_OPTIONS,
    DEFAULT_PAGE_SIZE,
  }
}

/**
 * Binder « nombre par page » pour une liste : centralise la coercition en Number,
 * la persistance et le rappel (reset page 1 + reload géré par le caller).
 * Évite de recopier le même bloc `changePageSize` dans chaque liste.
 *
 * @param {string} listKey - Clé de la liste (whitelistée côté backend)
 * @param {(size:number)=>any} [onChange] - Effet après changement (reset + reload)
 * @param {import('vue').Ref<number>} [targetRef] - Ref de taille existante à
 *   réutiliser (cas où elle est détenue par un sous-composable `*Data`) ; sinon
 *   une ref locale est créée, initialisée depuis la préférence enregistrée.
 * @returns {{ pageSize: import('vue').Ref<number>, changePageSize: (size:any)=>Promise<void> }}
 */
export function usePageSizePref(listKey, onChange, targetRef = null) {
  const { getPageSize, setPageSize } = useListPreferences()
  const pageSize = targetRef || ref(getPageSize(listKey))

  const changePageSize = async (size) => {
    pageSize.value = Number(size)
    setPageSize(listKey, pageSize.value)
    await onChange?.(pageSize.value)
  }

  return { pageSize, changePageSize }
}
